import { logger } from '@/lib/logger'
import { MMKV } from 'react-native-mmkv'
import type { StateStorage } from 'zustand/middleware'

const storage = new MMKV()

export type StoragePrimitive = string | number | boolean | Uint8Array

export const getStringFromStorage = (key: string): string | undefined => {
	try {
		return storage.getString(key)
	} catch (err) {
		logger.error(new Error('failed_to_get_storage_string', { cause: err }))
		return undefined
	}
}

export const getNumberFromStorage = (key: string): number | undefined => {
	try {
		return storage.getNumber(key)
	} catch (err) {
		logger.error(new Error('failed_to_get_storage_number', { cause: err }))
		return undefined
	}
}

export const getBooleanFromStorage = (key: string): boolean | undefined => {
	try {
		return storage.getBoolean(key)
	} catch (err) {
		logger.error(new Error('failed_to_get_storage_boolean', { cause: err }))
		return undefined
	}
}

export const saveToStorage = (
	key: string,
	value: StoragePrimitive | Record<string, StoragePrimitive>
): boolean => {
	try {
		const storageValue = typeof value === 'object' ? JSON.stringify(value) : value
		storage.set(key, storageValue)
		return true
	} catch (err) {
		logger.error(new Error('failed_to_save_storage_string', { cause: err }))
		return false
	}
}

export const loadFromStorage = (key: string): StoragePrimitive | null => {
	try {
		const value = getStringFromStorage(key)
		return value ? JSON.parse(value) : null
	} catch (err) {
		logger.error(new Error('failed_to_load_from_storage', { cause: err }))
		return null
	}
}

export const deleteFromStorage = (key: string): void => {
	try {
		storage.delete(key)
	} catch (err) {
		logger.error(new Error('failed_to_delete_from_storage', { cause: err }))
	}
}

export const clearStorage = () => {
	try {
		storage.clearAll()
	} catch (err) {
		logger.error(new Error('failed_to_clear_storage', { cause: err }))
	}
}

export const existsInStorage = (key: string): boolean => {
	try {
		return storage.contains(key)
	} catch (err) {
		logger.error(new Error('failed_to_check_contains_from_storage', { cause: err }))
		return false
	}
}

export const zustandStorage: StateStorage = {
	setItem: (name, value) => {
		return saveToStorage(name, value)
	},
	getItem: (name) => {
		return getStringFromStorage(name) ?? null
	},
	removeItem: (name) => {
		return deleteFromStorage(name)
	},
}

export const subscribeToStorageUpdates = storage.addOnValueChangedListener
